/**
 * Progress Metrics component - displays tutorial progress information
 */

import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "#app/utils/misc";
import { type TutorialProgress } from "./ProgressTracker";

export interface ProgressMetricsProps {
  progress: TutorialProgress | null;
  totalSteps: number;
  className?: string;
}

export function ProgressMetrics({
  progress,
  totalSteps,
  className,
}: ProgressMetricsProps) {
  const { t } = useTranslation("docs");
  if (totalSteps === 0) {
    return null;
  }

  const completedSteps = Math.min(
    new Set(progress?.completedSteps ?? []).size,
    totalSteps,
  );
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);
  const isCompleted = progress?.completed ?? false;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          {isCompleted ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-300" />
              <span className="font-medium text-green-700 dark:text-green-300">
                {t("completed")}
              </span>
            </>
          ) : (
            <span className="text-gray-600 dark:text-gray-400">
              {t("stepsCompleted", {
                completed: completedSteps,
                total: totalSteps,
              })}
            </span>
          )}
        </div>
        <span className="font-semibold text-[#141A42] dark:text-zinc-100 text-sm">
          {completionPercentage}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
        <div
          className={cn(
            "h-full transition-all duration-300 rounded-full",
            isCompleted
              ? "bg-green-500"
              : "bg-[linear-gradient(90deg,#5B66AA_0%,#909CCA_100%)]",
          )}
          style={{ width: `${completionPercentage}%` }}
        />
      </div>
    </div>
  );
}
